import { Fragment, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { Icon } from '../components/Icon.tsx';
import { Shell } from '../components/Shell.tsx';
import { Button, Confirm, Dialog, EmptyState, Field, IconButton, Skeleton, Switch } from '../components/ui.tsx';
import { t, tn, type MessageKey } from '../i18n/index.ts';
import { action, subscribe } from '../lib/api.ts';
import { clock, day as dayText, since } from '../lib/format.ts';
import { useUi } from '../lib/ui-state.tsx';
import { useSave } from './manage/common.tsx';

/**
 * Discuss: the team's chat. Channels on the left, direct messages under them; the conversation
 * on the right with replies, @mentions, reactions, pins and saved messages. Everything new comes
 * in live; mentions and saved messages have a list of their own, and search looks through every
 * conversation the person can read.
 */

type Kind = 'channel' | 'direct' | 'group';
type View = 'mentions' | 'saved' | 'search';
const EMOJI = ['👍', '❤️', '😂', '🎉', '😮', '🙏'] as const;

interface Person {
  id: string;
  name: string;
  username: string;
  online: boolean;
}
interface Item {
  id: string;
  kind: Kind;
  name: string;
  description: string | null;
  private: boolean;
  everyone: boolean;
  archived: boolean;
  role: 'owner' | 'member';
  muted: boolean;
  unread: number;
  mentioned: number;
  preview: string;
  last_at: string;
  others: { id: string; name: string; online: boolean }[];
}
interface Home {
  me: string;
  self: { id: string; name: string; username: string };
  channels: Item[];
  direct: Item[];
  people: Person[];
  mentions: number;
  records: number;
  can: { manage: boolean };
}
interface Msg {
  id: string;
  kind: 'text' | 'system';
  author: string | null;
  author_name: string | null;
  body: string | null;
  at: string;
  edited: boolean;
  deleted: boolean;
  pinned: boolean;
  saved: boolean;
  reply_to: { id: string; author_name: string | null; body: string | null } | null;
  reactions: { emoji: string; count: number; mine: boolean; names: string[] }[];
}
interface Conv {
  id: string;
  kind: Kind;
  name: string;
  description: string | null;
  private: boolean;
  everyone: boolean;
  archived: boolean;
  member: boolean;
  role: 'owner' | 'member' | null;
  muted: boolean;
  members: (Person & { role: 'owner' | 'member' })[];
  messages: Msg[];
  more: boolean;
  newer: boolean;
  first_new: string | null;
  pinned: number;
  can: { post: boolean; manage: boolean; pin: boolean };
}
type Listed = Msg & { channel: { id: string; kind: Kind; name: string }; reason: 'mention' | 'reply' | null; read: boolean | null };

const initials = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]!.toUpperCase())
    .join('');
const TONES = ['blue', 'green', 'orange', 'purple', 'teal', 'red', 'yellow'];
const toneOf = (id: string | null) => TONES[id ? [...id].reduce((a, c) => a + c.charCodeAt(0), 0) % TONES.length : 0]!;

function Avatar({ id, name, online, size = 'md' }: { id: string | null; name: string; online?: boolean; size?: 'sm' | 'md' | 'lg' }) {
  return (
    <span className={`ds-avatar ${size} tone-${toneOf(id)}`} aria-hidden="true">
      {initials(name) || '?'}
      {online !== undefined ? <span className={`ds-presence${online ? ' on' : ''}`} /> : null}
    </span>
  );
}

function ConvIcon({ item }: { item: Pick<Item, 'kind' | 'private' | 'others'> }) {
  if (item.kind === 'channel') return <Icon name={item.private ? 'lock' : 'hash'} size={16} />;
  if (item.kind === 'group') return <Icon name="users" size={16} />;
  const o = item.others[0];
  return o ? <Avatar id={o.id} name={o.name} online={o.online} size="sm" /> : <Icon name="contact" size={16} />;
}

/** Text with @mentions picked out and web links made clickable; nothing else is interpreted. */
function Body({ text, people, me }: { text: string; people: Map<string, Person>; me: string }) {
  const parts: ReactNode[] = [];
  const re = /(https?:\/\/[^\s<]+[^\s<.,;:!?)'"])|(^|[\s(])@([a-z0-9._-]{2,40})/gi;
  let last = 0;
  for (const m of text.matchAll(re)) {
    const at = m.index!;
    if (m[1]) {
      parts.push(text.slice(last, at));
      parts.push(
        <a key={at} href={m[1]} target="_blank" rel="noreferrer noopener">
          {m[1]}
        </a>,
      );
      last = at + m[1].length;
    } else {
      const who = people.get(m[3]!.toLowerCase());
      if (!who) continue;
      const lead = m[2] ?? '';
      parts.push(text.slice(last, at) + lead);
      parts.push(
        <span key={at} className={`ds-mention${who.id === me ? ' me' : ''}`} title={who.name}>
          @{who.username}
        </span>,
      );
      last = at + m[0].length;
    }
  }
  parts.push(text.slice(last));
  return <>{parts}</>;
}

function systemLine(m: Msg) {
  const who = m.author_name ?? '';
  const at = m.body!.indexOf(':');
  const kind = at < 0 ? m.body! : m.body!.slice(0, at);
  const rest = at < 0 ? '' : m.body!.slice(at + 1);
  const known = ['created', 'joined', 'left', 'added', 'removed', 'renamed', 'archived', 'unarchived'];
  return known.includes(kind) ? t(`ds.sys_${kind}` as MessageKey, { who, names: rest }) : m.body!;
}

export function Discuss() {
  const [params, setParams] = useSearchParams();
  const conv = params.get('c');
  const view = (['mentions', 'saved', 'search'].includes(params.get('v') ?? '') ? params.get('v') : null) as View | null;
  const onRecords = params.get('v') === 'records';
  const jump = params.get('m');
  const [home, setHome] = useState<Home | null>(null);
  const { fail } = useUi();
  const [dialog, setDialog] = useState<'channel' | 'direct' | 'browse' | null>(null);
  const [q, setQ] = useState(params.get('q') ?? '');
  const [filter, setFilter] = useState('');
  const load = useCallback(() => action<Home>('discuss.home').then(setHome, fail), [fail]);
  const [pulse, setPulse] = useState(0);
  const openRef = useRef(conv);
  openRef.current = conv;
  useEffect(() => {
    void load();
  }, [load]);
  useEffect(() => {
    let timer = 0;
    // One stream for the page; bursts of events become one reload.
    const stop = subscribe(['discuss'], (_e, data: { channel?: string }) => {
      if (data?.channel && data.channel === openRef.current) setPulse((n) => n + 1);
      window.clearTimeout(timer);
      timer = window.setTimeout(() => void load(), 250);
    });
    const id = window.setInterval(() => void load(), 30_000);
    return () => {
      stop();
      window.clearInterval(id);
      window.clearTimeout(timer);
    };
  }, [load]);

  const go = (next: { c?: string | null; v?: View | 'records' | null; m?: string | null; q?: string | null }) => {
    const p = new URLSearchParams();
    if (next.c) p.set('c', next.c);
    if (next.v) p.set('v', next.v);
    if (next.m) p.set('m', next.m);
    if (next.q) p.set('q', next.q);
    setParams(p);
  };
  const people = useMemo(() => new Map([...(home?.people ?? []), ...(home ? [{ ...home.self, online: true }] : [])].map((p) => [p.username.toLowerCase(), p])), [home]);
  const match = (i: Item) => !filter.trim() || i.name.toLowerCase().includes(filter.trim().toLowerCase());
  const showing = !!conv || !!view;

  return (
    <Shell>
      <div className="page ds-page">
        <div className={`ds-shell${showing ? ' has-main' : ''}`}>
          <aside className="ds-side panel" aria-label={t('ds.title')}>
            <div className="ds-side-head">
              <h1>{t('ds.title')}</h1>
              <IconButton icon="plus" label={t('ds.new_message')} onClick={() => setDialog('direct')} />
            </div>
            <form
              className="ds-search"
              role="search"
              onSubmit={(e) => {
                e.preventDefault();
                if (q.trim().length >= 2) go({ v: 'search', q: q.trim() });
              }}
            >
              <Icon name="search" size={16} />
              <input className="input" type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('ds.search')} aria-label={t('ds.search')} />
            </form>
            {!home ? (
              <div className="ds-side-loading">
                <Skeleton h={28} />
                <Skeleton h={28} />
                <Skeleton h={28} />
              </div>
            ) : (
              <nav className="ds-nav">
                <button type="button" className={`ds-nav-row${view === 'mentions' ? ' active' : ''}`} onClick={() => go({ v: 'mentions' })}>
                  <Icon name="at" size={16} />
                  <span className="ds-nav-name">{t('ds.mentions')}</span>
                  {home.mentions ? <span className="count ds-count hot">{home.mentions}</span> : null}
                </button>
                <button type="button" className={`ds-nav-row${onRecords ? ' active' : ''}`} onClick={() => go({ v: 'records' })}>
                  <Icon name="invoice" size={16} />
                  <span className="ds-nav-name">{t('ds.on_records')}</span>
                  {home.records ? <span className="count ds-count hot">{home.records}</span> : null}
                </button>
                <button type="button" className={`ds-nav-row${view === 'saved' ? ' active' : ''}`} onClick={() => go({ v: 'saved' })}>
                  <Icon name="bookmark" size={16} />
                  <span className="ds-nav-name">{t('ds.saved')}</span>
                </button>
                {home.channels.length + home.direct.length > 8 ? (
                  <input className="input ds-filter" value={filter} onChange={(e) => setFilter(e.target.value)} placeholder={t('ds.filter')} aria-label={t('ds.filter')} />
                ) : null}
                <Section
                  title={t('ds.channels')}
                  actions={
                    <>
                      <IconButton icon="search" label={t('ds.browse')} onClick={() => setDialog('browse')} />
                      <IconButton icon="plus" label={t('ds.new_channel')} onClick={() => setDialog('channel')} />
                    </>
                  }
                >
                  {home.channels.filter(match).map((c) => (
                    <Row key={c.id} item={c} active={conv === c.id} onOpen={() => go({ c: c.id })} />
                  ))}
                </Section>
                <Section title={t('ds.direct')} actions={<IconButton icon="plus" label={t('ds.new_message')} onClick={() => setDialog('direct')} />}>
                  {home.direct.filter(match).map((c) => (
                    <Row key={c.id} item={c} active={conv === c.id} onOpen={() => go({ c: c.id })} />
                  ))}
                  {!home.direct.length ? (
                    <button type="button" className="ds-nav-row ds-nav-hint" onClick={() => setDialog('direct')}>
                      <Icon name="plus" size={16} />
                      <span className="ds-nav-name">{t('ds.first_direct')}</span>
                    </button>
                  ) : null}
                </Section>
              </nav>
            )}
          </aside>
          <section className="ds-main panel" aria-label={t('ds.conversation')}>
            {!home ? (
              <Skeleton h="100%" r="0" />
            ) : conv ? (
              <Conversation
                key={conv}
                id={conv}
                jump={jump}
                pulse={pulse}
                home={home}
                people={people}
                onBack={() => go({})}
                onLatest={() => go({ c: conv })}
                onChanged={load}
                onLeft={() => (go({}), void load())}
              />
            ) : onRecords ? (
              <RecordNotesList people={people} me={home.me} onBack={() => go({})} onChanged={load} />
            ) : view ? (
              <MessageList
                key={`${view}:${params.get('q') ?? ''}`}
                view={view}
                q={params.get('q') ?? ''}
                home={home}
                people={people}
                onOpen={(c, m) => go({ c, m })}
                onBack={() => go({})}
                onChanged={load}
              />
            ) : (
              <Welcome home={home} onOpen={(c) => go({ c })} onNew={() => setDialog('direct')} />
            )}
          </section>
        </div>
        {dialog === 'channel' && home ? <NewChannel home={home} onClose={() => setDialog(null)} onDone={(id) => (setDialog(null), void load(), go({ c: id }))} /> : null}
        {dialog === 'direct' && home ? <NewDirect home={home} onClose={() => setDialog(null)} onDone={(id) => (setDialog(null), void load(), go({ c: id }))} /> : null}
        {dialog === 'browse' && home ? <Browse onClose={() => setDialog(null)} onOpen={(id) => (setDialog(null), void load(), go({ c: id }))} onNew={() => setDialog('channel')} /> : null}
      </div>
    </Shell>
  );
}

function Section({ title, actions, children }: { title: string; actions: ReactNode; children: ReactNode }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="ds-section">
      <div className="ds-section-head">
        <button type="button" className="ds-section-toggle" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
          <Icon name={open ? 'down' : 'up'} size={14} />
          {title}
        </button>
        <span className="ds-section-acts">{actions}</span>
      </div>
      {open ? <div className="ds-section-body">{children}</div> : null}
    </div>
  );
}

function Row({ item, active, onOpen }: { item: Item; active: boolean; onOpen: () => void }) {
  const bold = item.unread > 0 && !item.muted;
  return (
    <button
      type="button"
      className={`ds-nav-row${active ? ' active' : ''}${bold ? ' unread' : ''}${item.muted ? ' muted' : ''}${item.archived ? ' archived' : ''}`}
      aria-current={active || undefined}
      onClick={onOpen}
    >
      <span className="ds-nav-icon">
        <ConvIcon item={item} />
      </span>
      <span className="ds-nav-name">{item.name}</span>
      {item.muted ? <Icon name="bellOff" size={14} /> : null}
      {item.mentioned ? (
        <span className="count ds-count hot" aria-label={tn('ds.mentions_n', item.mentioned)}>
          @{item.mentioned}
        </span>
      ) : bold ? (
        <span className={`count ds-count${item.kind !== 'channel' ? ' hot' : ''}`} aria-label={tn('ds.unread_n', item.unread)}>
          {item.unread}
        </span>
      ) : null}
    </button>
  );
}

function Welcome({ home, onOpen, onNew }: { home: Home; onOpen: (id: string) => void; onNew: () => void }) {
  const fresh = [...home.channels, ...home.direct].filter((c) => c.unread && !c.muted).sort((a, b) => b.last_at.localeCompare(a.last_at));
  return (
    <div className="ds-welcome">
      <EmptyState
        icon="messages"
        title={fresh.length ? t('ds.catch_up') : t('ds.all_read')}
        body={fresh.length ? tn('ds.catch_up_body', fresh.length) : t('ds.all_read_body')}
        action={
          <Button icon="plus" onClick={onNew}>
            {t('ds.new_message')}
          </Button>
        }
      />
      {fresh.length ? (
        <ul className="ds-fresh">
          {fresh.slice(0, 8).map((c) => (
            <li key={c.id}>
              <button type="button" onClick={() => onOpen(c.id)}>
                <span className="ds-nav-icon">
                  <ConvIcon item={c} />
                </span>
                <span className="ds-fresh-main">
                  <strong>{c.name}</strong>
                  <span className="muted">{c.preview}</span>
                </span>
                <span className="count ds-count hot">{c.unread}</span>
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

// ---- a conversation ---------------------------------------------------------------------------

function Conversation({
  id,
  jump,
  pulse,
  home,
  people,
  onBack,
  onLatest,
  onChanged,
  onLeft,
}: {
  id: string;
  jump: string | null;
  pulse: number;
  home: Home;
  people: Map<string, Person>;
  onBack: () => void;
  onLatest: () => void;
  onChanged: () => void;
  onLeft: () => void;
}) {
  const [c, setC] = useState<Conv | null>(null);
  const [older, setOlder] = useState<Msg[]>([]);
  const [olderMore, setOlderMore] = useState<boolean | null>(null);
  const [replyTo, setReplyTo] = useState<Msg | null>(null);
  const [panel, setPanel] = useState<'members' | 'pins' | 'settings' | null>(null);
  const [gone, setGone] = useState(false);
  const { fail } = useUi();
  const { run, busy } = useSave();
  const list = useRef<HTMLDivElement>(null);
  const atBottom = useRef(true);
  const firstLoad = useRef(true);
  const load = useCallback(
    () =>
      action<Conv>('discuss.channel', { id, around: firstLoad.current ? jump : null }).then(
        (r) => {
          setC(r);
          onChanged();
        },
        (e) => {
          if ((e as { status?: number }).status === 404) setGone(true);
          else fail(e);
        },
      ),
    [id, jump, fail, onChanged],
  );
  useEffect(() => {
    void load();
  }, [load]);
  useEffect(() => {
    if (pulse && !firstLoad.current) void load();
  }, [pulse, load]);
  // Stays at the newest message while the reader is there; a jump lands on its message.
  useEffect(() => {
    if (!c) return;
    const box = list.current;
    if (!box) return;
    if (firstLoad.current) {
      firstLoad.current = false;
      const target = jump ? box.querySelector(`[data-id="${jump}"]`) : c.first_new ? box.querySelector(`[data-id="${c.first_new}"]`) : null;
      if (target) target.scrollIntoView({ block: 'center' });
      else box.scrollTop = box.scrollHeight;
      return;
    }
    if (atBottom.current) box.scrollTop = box.scrollHeight;
  }, [c, jump]);

  if (gone)
    return (
      <div className="ds-conv-empty">
        <EmptyState icon="lock" title={t('ds.gone')} body={t('ds.gone_body')} action={<Button onClick={onBack}>{t('ds.back')}</Button>} />
      </div>
    );
  if (!c)
    return (
      <div className="ds-conv">
        <div className="ds-conv-head">
          <Skeleton h={32} w={220} />
        </div>
        <div className="ds-msgs">
          <Skeleton h={60} />
          <Skeleton h={60} />
        </div>
      </div>
    );

  const all = [...older, ...c.messages];
  const loadOlder = async () => {
    const first = all[0];
    if (!first) return;
    const box = list.current;
    const h = box?.scrollHeight ?? 0;
    const r = await action<Conv>('discuss.channel', { id, before: first.at }).catch(fail);
    if (!r) return;
    setOlder((o) => [...r.messages, ...o]);
    setOlderMore(r.more);
    requestAnimationFrame(() => box && (box.scrollTop = box.scrollHeight - h));
  };
  const hasMore = olderMore ?? c.more;
  const act = async (name: string, input: Record<string, unknown>, done?: string) => {
    if (await run(() => action(name, input), done)) {
      setOlder([]);
      setOlderMore(null);
      await load();
      return true;
    }
    return false;
  };
  const others = c.members.filter((m) => m.id !== home.me);
  const title = c.kind === 'channel' ? c.name : c.name || t('ds.only_you');

  return (
    <div className="ds-conv">
      <header className="ds-conv-head">
        <IconButton icon="back" label={t('ds.back')} className="ds-back" onClick={onBack} />
        <span className="ds-conv-icon">
          {c.kind === 'direct' && others[0] ? (
            <Avatar id={others[0].id} name={others[0].name} online={others[0].online} />
          ) : (
            <Icon name={c.kind === 'group' ? 'users' : c.private ? 'lock' : 'hash'} size={20} />
          )}
        </span>
        <div className="ds-conv-title">
          <h2>{title}</h2>
          <p className="muted">
            {c.kind === 'direct' && others[0] ? (others[0].online ? t('ds.online') : `@${others[0].username}`) : (c.description ?? (c.private ? t('ds.private_channel') : t('ds.open_channel')))}
          </p>
        </div>
        <div className="ds-conv-acts">
          {c.kind !== 'direct' ? (
            <button type="button" className="ds-members-btn" onClick={() => setPanel('members')} aria-label={tn('ds.members_n', c.members.length)} title={tn('ds.members_n', c.members.length)}>
              <span className="ds-stack" aria-hidden="true">
                {c.members.slice(0, 3).map((m) => (
                  <Avatar key={m.id} id={m.id} name={m.name} size="sm" />
                ))}
              </span>
              <span>{c.members.length}</span>
            </button>
          ) : null}
          <IconButton icon="pushpin" label={tn('ds.pinned_n', c.pinned)} onClick={() => setPanel('pins')} className={c.pinned ? 'ds-has-pins' : ''} />
          {c.member ? (
            <IconButton
              icon={c.muted ? 'bellOff' : 'bell'}
              label={c.muted ? t('ds.unmute') : t('ds.mute')}
              onClick={() => void act('discuss.mute', { id: c.id, muted: !c.muted }, c.muted ? t('ds.unmuted') : t('ds.muted'))}
            />
          ) : null}
          {c.kind === 'channel' && c.member ? <IconButton icon="settings" label={t('ds.settings')} onClick={() => setPanel('settings')} /> : null}
        </div>
      </header>

      <div
        className="ds-msgs"
        ref={list}
        onScroll={(e) => {
          const b = e.currentTarget;
          atBottom.current = b.scrollHeight - b.scrollTop - b.clientHeight < 80;
        }}
        aria-live="polite"
        aria-relevant="additions"
      >
        {hasMore ? (
          <div className="ds-older">
            <Button size="sm" variant="ghost" icon="up" onClick={() => void loadOlder()}>
              {t('ds.older')}
            </Button>
          </div>
        ) : (
          <div className="ds-start">
            <span className="ds-start-icon">
              <Icon name={c.kind === 'channel' ? 'hash' : 'messages'} size={28} />
            </span>
            <h3>{c.kind === 'channel' ? t('ds.start_channel', { name: c.name }) : t('ds.start_direct', { names: title })}</h3>
            <p className="muted">{c.kind === 'channel' ? (c.description ?? t('ds.start_channel_body')) : t('ds.start_direct_body')}</p>
          </div>
        )}
        {all.map((m, i) => {
          const prev = all[i - 1];
          const newDay = !prev || new Date(prev.at).toDateString() !== new Date(m.at).toDateString();
          const compact =
            !newDay && prev && prev.kind === 'text' && m.kind === 'text' && prev.author === m.author && !m.reply_to && new Date(m.at).getTime() - new Date(prev.at).getTime() < 5 * 60_000;
          return (
            <Fragment key={m.id}>
              {newDay ? (
                <div className="ds-day" role="separator">
                  <span>{dayLabel(m.at)}</span>
                </div>
              ) : null}
              {c.first_new === m.id ? (
                <div className="ds-new" role="separator">
                  <span>{t('ds.new_messages')}</span>
                </div>
              ) : null}
              {m.kind === 'system' ? (
                <p className="ds-system" data-id={m.id}>
                  {systemLine(m)} · <time dateTime={m.at}>{clock(m.at)}</time>
                </p>
              ) : (
                <MessageRow m={m} compact={!!compact} highlight={jump === m.id} me={home.me} people={people} conv={c} onReply={() => setReplyTo(m)} act={act} busy={busy} />
              )}
            </Fragment>
          );
        })}
        {c.newer ? (
          <div className="ds-older">
            <Button size="sm" variant="ghost" icon="down" onClick={onLatest}>
              {t('ds.latest')}
            </Button>
          </div>
        ) : null}
      </div>

      {c.archived ? (
        <div className="ds-bar">
          <Icon name="archive" size={16} />
          <span>{t('ds.archived_bar')}</span>
          {c.can.manage ? (
            <Button size="sm" onClick={() => void act('discuss.archive', { id: c.id, archived: false }, t('ds.unarchived'))}>
              {t('ds.unarchive')}
            </Button>
          ) : null}
        </div>
      ) : !c.member ? (
        <div className="ds-bar">
          <Icon name="hash" size={16} />
          <span>{t('ds.join_bar', { name: c.name })}</span>
          <Button size="sm" variant="primary" busy={busy} onClick={() => void act('discuss.join', { id: c.id }).then((ok) => ok && onChanged())}>
            {t('ds.join')}
          </Button>
        </div>
      ) : (
        <Composer
          key={c.id}
          conv={c}
          people={home.people}
          replyTo={replyTo}
          onCancelReply={() => setReplyTo(null)}
          onSent={async () => {
            setReplyTo(null);
            atBottom.current = true;
            await load();
          }}
        />
      )}

      {panel === 'members' ? <Members conv={c} home={home} onClose={() => setPanel(null)} act={act} /> : null}
      {panel === 'pins' ? <Pins conv={c} people={people} me={home.me} onClose={() => setPanel(null)} act={act} /> : null}
      {panel === 'settings' ? <ChannelSettings conv={c} home={home} onClose={() => setPanel(null)} act={act} onLeft={onLeft} /> : null}
    </div>
  );
}

function dayLabel(iso: string) {
  const d = new Date(iso);
  const today = new Date();
  const y = new Date();
  y.setDate(today.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return t('ds.today');
  if (d.toDateString() === y.toDateString()) return t('ds.yesterday');
  return dayText(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`);
}

function MessageRow({
  m,
  compact,
  highlight,
  me,
  people,
  conv,
  onReply,
  act,
  busy,
}: {
  m: Msg;
  compact: boolean;
  highlight: boolean;
  me: string;
  people: Map<string, Person>;
  conv: Conv;
  onReply: () => void;
  act: (name: string, input: Record<string, unknown>, done?: string) => Promise<boolean>;
  busy: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(m.body ?? '');
  const [picking, setPicking] = useState(false);
  const [removing, setRemoving] = useState(false);
  const mine = m.author === me;
  const canAct = conv.member && !conv.archived && !m.deleted;
  const save = async () => {
    const text = draft.trim();
    if (!text) return;
    if (await act('discuss.edit', { id: m.id, body: text })) setEditing(false);
  };
  return (
    <article className={`ds-msg${compact ? ' compact' : ''}${highlight ? ' highlight' : ''}${m.pinned ? ' pinned' : ''}`} data-id={m.id} tabIndex={-1}>
      <div className="ds-msg-side">
        {compact ? (
          <time className="ds-msg-hover-time" dateTime={m.at}>
            {clock(m.at)}
          </time>
        ) : (
          <Avatar id={m.author} name={m.author_name ?? '?'} />
        )}
      </div>
      <div className="ds-msg-main">
        {!compact ? (
          <div className="ds-msg-head">
            <strong>{m.author_name ?? t('ds.someone')}</strong>
            <time dateTime={m.at}>{clock(m.at)}</time>
            {m.pinned ? (
              <span className="ds-pin-tag">
                <Icon name="pushpin" size={12} /> {t('ds.pinned')}
              </span>
            ) : null}
          </div>
        ) : null}
        {m.reply_to ? (
          <div className="ds-quote">
            <Icon name="reply" size={12} />
            <strong>{m.reply_to.author_name ?? t('ds.someone')}</strong>
            <span>{m.reply_to.body ?? t('ds.deleted_short')}</span>
          </div>
        ) : null}
        {editing ? (
          <div className="ds-edit">
            <textarea
              className="input textarea"
              value={draft}
              rows={Math.min(8, Math.max(2, draft.split('\n').length))}
              maxLength={4000}
              autoFocus
              aria-label={t('ds.edit')}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') setEditing(false);
                if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
                  e.preventDefault();
                  void save();
                }
              }}
            />
            <div className="ds-edit-acts">
              <span className="muted small">{t('ds.edit_hint')}</span>
              <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>
                {t('ds.cancel')}
              </Button>
              <Button size="sm" variant="primary" busy={busy} onClick={() => void save()}>
                {t('ds.save')}
              </Button>
            </div>
          </div>
        ) : m.deleted ? (
          <p className="ds-text deleted">{t('ds.deleted')}</p>
        ) : (
          <p className="ds-text">
            <Body text={m.body ?? ''} people={people} me={me} />
            {m.edited ? <span className="ds-edited"> {t('ds.edited')}</span> : null}
          </p>
        )}
        {m.reactions.length ? (
          <div className="ds-reactions">
            {m.reactions.map((r) => (
              <button
                key={r.emoji}
                type="button"
                className={`ds-reaction${r.mine ? ' mine' : ''}`}
                aria-pressed={r.mine}
                disabled={!canAct}
                title={r.names.join(', ')}
                aria-label={t('ds.reacted', { emoji: r.emoji, names: r.names.join(', ') })}
                onClick={() => void act('discuss.react', { id: m.id, emoji: r.emoji })}
              >
                <span aria-hidden="true">{r.emoji}</span> {r.count}
              </button>
            ))}
          </div>
        ) : null}
      </div>
      {canAct && !editing ? (
        <div className="ds-tools" role="toolbar" aria-label={t('ds.message_tools')}>
          <div className="ds-picker-wrap">
            <IconButton icon="smile" label={t('ds.react')} onClick={() => setPicking((p) => !p)} />
            {picking ? (
              <div className="ds-picker" role="menu" onMouseLeave={() => setPicking(false)}>
                {EMOJI.map((e) => (
                  <button
                    key={e}
                    type="button"
                    role="menuitem"
                    aria-label={t('ds.react_with', { emoji: e })}
                    onClick={() => {
                      setPicking(false);
                      void act('discuss.react', { id: m.id, emoji: e });
                    }}
                  >
                    {e}
                  </button>
                ))}
              </div>
            ) : null}
          </div>
          <IconButton icon="reply" label={t('ds.reply')} onClick={onReply} />
          <IconButton
            icon="bookmark"
            label={m.saved ? t('ds.unsave') : t('ds.save_msg')}
            className={m.saved ? 'ds-on' : ''}
            onClick={() => void act('discuss.save', { id: m.id, saved: !m.saved }, m.saved ? t('ds.unsaved') : t('ds.saved_ok'))}
          />
          {conv.can.pin ? (
            <IconButton
              icon="pushpin"
              label={m.pinned ? t('ds.unpin') : t('ds.pin')}
              className={m.pinned ? 'ds-on' : ''}
              onClick={() => void act('discuss.pin', { id: m.id, pinned: !m.pinned }, m.pinned ? t('ds.unpinned') : t('ds.pinned_ok'))}
            />
          ) : null}
          {mine ? (
            <IconButton
              icon="edit"
              label={t('ds.edit')}
              onClick={() => {
                setDraft(m.body ?? '');
                setEditing(true);
              }}
            />
          ) : null}
          {mine || conv.can.manage ? <IconButton icon="trash" label={t('ds.delete')} onClick={() => setRemoving(true)} /> : null}
        </div>
      ) : null}
      {removing ? (
        <Confirm
          danger
          title={t('ds.delete_q')}
          body={mine ? t('ds.delete_body') : t('ds.delete_body_other', { name: m.author_name ?? '' })}
          confirm={t('ds.delete')}
          busy={busy}
          onClose={() => setRemoving(false)}
          onConfirm={async () => {
            if (await act('discuss.delete', { id: m.id }, t('ds.deleted_ok'))) setRemoving(false);
          }}
        />
      ) : null}
    </article>
  );
}

function Composer({ conv, people, replyTo, onCancelReply, onSent }: { conv: Conv; people: Person[]; replyTo: Msg | null; onCancelReply: () => void; onSent: () => Promise<void> }) {
  const key = `erp.discuss.draft.${conv.id}`;
  const [body, setBody] = useState(() => {
    try {
      return localStorage.getItem(key) ?? '';
    } catch {
      return '';
    }
  });
  const [pick, setPick] = useState(0);
  const [caret, setCaret] = useState(0);
  const { fail } = useUi();
  const [sending, setSending] = useState(false);
  const input = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    try {
      if (body) localStorage.setItem(key, body);
      else localStorage.removeItem(key);
    } catch {
      // The draft lasts as long as the page.
    }
  }, [body, key]);
  useEffect(() => {
    if (replyTo) input.current?.focus();
  }, [replyTo]);
  // "@mi" just before the caret offers the people whose name or username starts that way.
  const token = /(?:^|[\s(])@([a-z0-9._-]*)$/i.exec(body.slice(0, caret));
  const pool = conv.kind === 'channel' && !conv.private ? people : conv.members;
  const matches = token
    ? pool
        .filter((p) => {
          const q = token[1]!.toLowerCase();
          return (
            p.username.toLowerCase().startsWith(q) ||
            p.name
              .toLowerCase()
              .split(/\s+/)
              .some((w) => w.startsWith(q))
          );
        })
        .slice(0, 6)
    : [];
  useEffect(() => setPick(0), [token?.[1]]);
  const choose = (p: Person) => {
    const start = caret - token![1]!.length;
    const next = `${body.slice(0, start)}${p.username} ${body.slice(caret)}`;
    setBody(next);
    const pos = start + p.username.length + 1;
    requestAnimationFrame(() => {
      input.current?.setSelectionRange(pos, pos);
      input.current?.focus();
      setCaret(pos);
    });
  };
  const send = async () => {
    const text = body.trim();
    if (!text || sending) return;
    setSending(true);
    try {
      await action('discuss.post', { channel: conv.id, body: text, reply_to: replyTo?.id ?? null });
      setBody('');
      await onSent();
    } catch (e) {
      fail(e);
    } finally {
      setSending(false);
      input.current?.focus();
    }
  };
  const placeholder = conv.kind === 'channel' ? t('ds.write_channel', { name: conv.name }) : t('ds.write_to', { names: conv.name });
  return (
    <form
      className="ds-composer"
      onSubmit={(e) => {
        e.preventDefault();
        void send();
      }}
    >
      {replyTo ? (
        <div className="ds-replying">
          <Icon name="reply" size={14} />
          <span>
            {t('ds.replying_to', { name: replyTo.author_name ?? '' })} <span className="muted">{(replyTo.body ?? '').slice(0, 80)}</span>
          </span>
          <IconButton icon="x" label={t('ds.cancel_reply')} onClick={onCancelReply} />
        </div>
      ) : null}
      {matches.length ? (
        <ul className="ds-suggest" role="listbox" aria-label={t('ds.mention_someone')}>
          {matches.map((p, i) => (
            <li key={p.id} role="option" aria-selected={i === pick}>
              <button type="button" className={i === pick ? 'active' : ''} onMouseDown={(e) => e.preventDefault()} onClick={() => choose(p)}>
                <Avatar id={p.id} name={p.name} online={p.online} size="sm" />
                <strong>{p.name}</strong>
                <span className="muted">@{p.username}</span>
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      <div className="ds-composer-box">
        <textarea
          ref={input}
          className="input textarea"
          rows={Math.min(8, Math.max(1, body.split('\n').length))}
          maxLength={4000}
          value={body}
          placeholder={placeholder}
          aria-label={placeholder}
          onChange={(e) => {
            setBody(e.target.value);
            setCaret(e.target.selectionStart);
          }}
          onSelect={(e) => setCaret(e.currentTarget.selectionStart)}
          onKeyDown={(e) => {
            if (matches.length && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
              e.preventDefault();
              setPick((p) => (p + (e.key === 'ArrowDown' ? 1 : matches.length - 1)) % matches.length);
            } else if (matches.length && (e.key === 'Enter' || e.key === 'Tab')) {
              e.preventDefault();
              choose(matches[pick]!);
            } else if (e.key === 'Escape' && replyTo) onCancelReply();
            else if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
              e.preventDefault();
              void send();
            }
          }}
        />
        <IconButton
          icon="at"
          label={t('ds.mention_someone')}
          onClick={() => {
            const next = `${body}${body && !/\s$/.test(body) ? ' ' : ''}@`;
            setBody(next);
            setCaret(next.length);
            requestAnimationFrame(() => {
              input.current?.focus();
              input.current?.setSelectionRange(next.length, next.length);
            });
          }}
        />
        <button type="submit" className="ds-send" aria-label={t('ds.send')} title={t('ds.send')} disabled={!body.trim() || sending}>
          <Icon name="send" size={18} />
        </button>
      </div>
      <p className="ds-hint muted">{t('ds.hint')}</p>
    </form>
  );
}

// ---- mentions, saved and search ---------------------------------------------------------------

function MessageList({
  view,
  q,
  home,
  people,
  onOpen,
  onBack,
  onChanged,
}: {
  view: View;
  q: string;
  home: Home;
  people: Map<string, Person>;
  onOpen: (c: string, m: string) => void;
  onBack: () => void;
  onChanged: () => void;
}) {
  const [items, setItems] = useState<Listed[] | null>(null);
  const { fail } = useUi();
  const { run, busy } = useSave();
  const load = useCallback(() => {
    const name = view === 'mentions' ? 'discuss.mentions' : view === 'saved' ? 'discuss.saved' : 'discuss.search';
    return action<{ items: Listed[] }>(name, view === 'search' ? { q } : {}).then((r) => setItems(r.items), fail);
  }, [view, q, fail]);
  useEffect(() => {
    void load();
  }, [load]);
  const unread = items?.filter((i) => i.read === false).length ?? 0;
  const title = view === 'mentions' ? t('ds.mentions') : view === 'saved' ? t('ds.saved') : t('ds.results', { q });
  return (
    <div className="ds-conv">
      <header className="ds-conv-head">
        <IconButton icon="back" label={t('ds.back')} className="ds-back" onClick={onBack} />
        <span className="ds-conv-icon">
          <Icon name={view === 'mentions' ? 'at' : view === 'saved' ? 'bookmark' : 'search'} size={20} />
        </span>
        <div className="ds-conv-title">
          <h2>{title}</h2>
          <p className="muted">{view === 'mentions' ? t('ds.mentions_sub') : view === 'saved' ? t('ds.saved_sub') : items ? tn('ds.found', items.length) : ''}</p>
        </div>
        {view === 'mentions' && unread ? (
          <div className="ds-conv-acts">
            <Button size="sm" icon="check" busy={busy} onClick={async () => (await run(() => action('discuss.read_mentions'))) && (void load(), onChanged())}>
              {t('ds.mark_read')}
            </Button>
          </div>
        ) : null}
      </header>
      <div className="ds-msgs ds-list">
        {!items ? (
          <Skeleton h={200} />
        ) : !items.length ? (
          <EmptyState
            icon={view === 'mentions' ? 'at' : view === 'saved' ? 'bookmark' : 'search'}
            title={view === 'mentions' ? t('ds.no_mentions') : view === 'saved' ? t('ds.no_saved') : t('ds.no_results')}
            body={view === 'mentions' ? t('ds.no_mentions_body') : view === 'saved' ? t('ds.no_saved_body') : t('ds.no_results_body')}
          />
        ) : (
          <ul className="ds-cards">
            {items.map((m) => (
              <li key={m.id}>
                <button type="button" className={`ds-card${m.read === false ? ' unread' : ''}`} onClick={() => onOpen(m.channel.id, m.id)}>
                  <span className="ds-card-where">
                    <Icon name={m.channel.kind === 'channel' ? 'hash' : m.channel.kind === 'group' ? 'users' : 'messages'} size={14} />
                    {m.channel.name}
                    {m.reason ? <span className="tag ds-reason">{m.reason === 'reply' ? t('ds.reason_reply') : t('ds.reason_mention')}</span> : null}
                    <time dateTime={m.at}>{since(m.at)}</time>
                  </span>
                  <span className="ds-card-body">
                    <Avatar id={m.author} name={m.author_name ?? '?'} size="sm" />
                    <span>
                      <strong>{m.author_name}</strong>
                      <span className="ds-text">
                        <Body text={m.body ?? ''} people={people} me={home.me} />
                      </span>
                    </span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

// ---- notes on records -------------------------------------------------------------------------

interface RecordNote {
  id: string;
  reason: 'mention' | 'follow';
  read: boolean;
  title: string;
  link: string;
  author_name: string | null;
  body: string;
  at: string;
}

/** Notes written on invoices, orders, customers and leads that mention me or that I follow. */
function RecordNotesList({ people, me, onBack, onChanged }: { people: Map<string, Person>; me: string; onBack: () => void; onChanged: () => void }) {
  const [items, setItems] = useState<RecordNote[] | null>(null);
  const { fail } = useUi();
  const { run, busy } = useSave();
  const navigate = useNavigate();
  const load = useCallback(() => action<{ items: RecordNote[] }>('discuss.record_notes').then((r) => setItems(r.items), fail), [fail]);
  useEffect(() => {
    void load();
  }, [load]);
  const unread = items?.filter((i) => !i.read).length ?? 0;
  return (
    <div className="ds-conv">
      <header className="ds-conv-head">
        <IconButton icon="back" label={t('ds.back')} className="ds-back" onClick={onBack} />
        <span className="ds-conv-icon">
          <Icon name="invoice" size={20} />
        </span>
        <div className="ds-conv-title">
          <h2>{t('ds.on_records')}</h2>
          <p className="muted">{t('ds.on_records_sub')}</p>
        </div>
        {unread ? (
          <div className="ds-conv-acts">
            <Button size="sm" icon="check" busy={busy} onClick={async () => (await run(() => action('discuss.read_record_notes'))) && (void load(), onChanged())}>
              {t('ds.mark_all_read')}
            </Button>
          </div>
        ) : null}
      </header>
      <div className="ds-msgs ds-list">
        {!items ? (
          <Skeleton h={200} />
        ) : !items.length ? (
          <EmptyState icon="invoice" title={t('ds.no_record_notes')} body={t('ds.no_record_notes_body')} />
        ) : (
          <ul className="ds-cards">
            {items.map((m) => (
              <li key={m.id}>
                <button type="button" className={`ds-card${m.read ? '' : ' unread'}`} onClick={() => navigate(m.link)}>
                  <span className="ds-card-where">
                    <Icon name="invoice" size={14} />
                    {m.title}
                    <span className="tag ds-reason">{m.reason === 'mention' ? t('ds.mentioned_you') : t('ds.wrote_on')}</span>
                    <time dateTime={m.at}>{since(m.at)}</time>
                  </span>
                  <span className="ds-card-body">
                    <Avatar id={null} name={m.author_name ?? '?'} size="sm" />
                    <span>
                      <strong>{m.author_name}</strong>
                      <span className="ds-text">
                        <Body text={m.body} people={people} me={me} />
                      </span>
                    </span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

// ---- dialogs ----------------------------------------------------------------------------------

function PeoplePicker({ people, selected, onChange, max, exclude = [] }: { people: Person[]; selected: string[]; onChange: (ids: string[]) => void; max?: number; exclude?: string[] }) {
  const [q, setQ] = useState('');
  const shown = people.filter((p) => !exclude.includes(p.id) && (!q.trim() || `${p.name} ${p.username}`.toLowerCase().includes(q.trim().toLowerCase())));
  const full = max !== undefined && selected.length >= max;
  return (
    <div className="ds-picker-people">
      {selected.length ? (
        <div className="ds-chosen">
          {selected.map((id) => {
            const p = people.find((x) => x.id === id);
            return p ? (
              <button key={id} type="button" className="chip active" onClick={() => onChange(selected.filter((x) => x !== id))} aria-label={t('ds.unchoose', { name: p.name })}>
                {p.name} <Icon name="x" size={12} />
              </button>
            ) : null;
          })}
        </div>
      ) : null}
      <div className="ds-search">
        <Icon name="search" size={16} />
        <input className="input" type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('ds.find_people')} aria-label={t('ds.find_people')} autoFocus />
      </div>
      <ul className="ds-people">
        {shown.map((p) => {
          const on = selected.includes(p.id);
          return (
            <li key={p.id}>
              <label className={`ds-person${on ? ' on' : ''}${!on && full ? ' off' : ''}`}>
                <input type="checkbox" checked={on} disabled={!on && full} onChange={() => onChange(on ? selected.filter((x) => x !== p.id) : [...selected, p.id])} />
                <Avatar id={p.id} name={p.name} online={p.online} size="sm" />
                <span className="ds-person-name">
                  <strong>{p.name}</strong>
                  <span className="muted">@{p.username}</span>
                </span>
              </label>
            </li>
          );
        })}
        {!shown.length ? <li className="muted ds-people-none">{t('ds.nobody_found')}</li> : null}
      </ul>
      {full ? <p className="muted small">{t('ds.group_full')}</p> : null}
    </div>
  );
}

function NewDirect({ home, onClose, onDone }: { home: Home; onClose: () => void; onDone: (id: string) => void }) {
  const [sel, setSel] = useState<string[]>([]);
  const { run, busy } = useSave();
  const start = async () => {
    let id = '';
    if (await run(async () => (id = (await action<{ id: string }>('discuss.direct', { users: sel })).id))) onDone(id);
  };
  return (
    <Dialog
      title={t('ds.new_message')}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('ds.cancel')}
          </Button>
          <Button variant="primary" busy={busy} disabled={!sel.length} onClick={() => void start()}>
            {sel.length > 1 ? t('ds.start_group') : t('ds.start')}
          </Button>
        </>
      }
    >
      <p className="muted small ds-dialog-intro">{t('ds.new_message_hint')}</p>
      <PeoplePicker people={home.people} selected={sel} onChange={setSel} max={7} />
    </Dialog>
  );
}

function NewChannel({ home, onClose, onDone }: { home: Home; onClose: () => void; onDone: (id: string) => void }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [priv, setPriv] = useState(false);
  const [sel, setSel] = useState<string[]>([]);
  const [tried, setTried] = useState(false);
  const { run, busy } = useSave();
  const clean = name.trim().replace(/^#+/, '').trim();
  const error = !clean ? t('ds.need_name') : null;
  const create = async () => {
    setTried(true);
    if (error) return;
    let id = '';
    if (await run(async () => (id = (await action<{ id: string }>('discuss.create_channel', { name: clean, description, private: priv, members: sel })).id), t('ds.channel_made'))) onDone(id);
  };
  return (
    <Dialog
      title={t('ds.new_channel')}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('ds.cancel')}
          </Button>
          <Button variant="primary" busy={busy} onClick={() => void create()}>
            {t('ds.create')}
          </Button>
        </>
      }
    >
      <Field label={t('ds.f_name')} hint={t('ds.f_name_hint')} required error={tried ? error : null}>
        <input className="input" value={name} maxLength={41} onChange={(e) => setName(e.target.value)} placeholder={t('ds.f_name_ph')} />
      </Field>
      <Field label={t('ds.f_description')}>
        <input className="input" value={description} maxLength={300} onChange={(e) => setDescription(e.target.value)} placeholder={t('ds.f_description_ph')} />
      </Field>
      <div className="ds-setting">
        <span>
          <strong>{t('ds.f_private')}</strong>
          <span className="muted small">{priv ? t('ds.f_private_on') : t('ds.f_private_off')}</span>
        </span>
        <Switch checked={priv} onChange={setPriv} label={t('ds.f_private')} />
      </div>
      <p className="field-label ds-add-label">{t('ds.f_members')}</p>
      <PeoplePicker people={home.people} selected={sel} onChange={setSel} />
    </Dialog>
  );
}

function Browse({ onClose, onOpen, onNew }: { onClose: () => void; onOpen: (id: string) => void; onNew: () => void }) {
  const [items, setItems] = useState<{ id: string; name: string; description: string | null; archived: boolean; members: number; joined: boolean }[] | null>(null);
  const { fail } = useUi();
  const { run, busy } = useSave();
  useEffect(() => {
    action<{ items: NonNullable<typeof items> }>('discuss.browse').then((r) => setItems(r.items), fail);
  }, [fail]);
  return (
    <Dialog
      title={t('ds.browse')}
      onClose={onClose}
      footer={
        <Button icon="plus" onClick={onNew}>
          {t('ds.new_channel')}
        </Button>
      }
    >
      {!items ? (
        <Skeleton h={200} />
      ) : (
        <ul className="ds-browse">
          {items.map((c) => (
            <li key={c.id} className={c.archived ? 'archived' : ''}>
              <Icon name="hash" size={18} />
              <span className="ds-browse-main">
                <strong>{c.name}</strong>
                <span className="muted small">
                  {tn('ds.members_n', c.members)}
                  {c.archived ? ` · ${t('ds.archived')}` : ''}
                  {c.description ? ` · ${c.description}` : ''}
                </span>
              </span>
              {c.joined || c.archived ? (
                <Button size="sm" variant="ghost" onClick={() => onOpen(c.id)}>
                  {t('ds.open')}
                </Button>
              ) : (
                <Button size="sm" variant="primary" busy={busy} onClick={async () => (await run(() => action('discuss.join', { id: c.id }))) && onOpen(c.id)}>
                  {t('ds.join')}
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
    </Dialog>
  );
}

type Act = (name: string, input: Record<string, unknown>, done?: string) => Promise<boolean>;

function Members({ conv, home, onClose, act }: { conv: Conv; home: Home; onClose: () => void; act: Act }) {
  const [adding, setAdding] = useState<string[] | null>(null);
  const canAdd = conv.kind === 'channel' && conv.member && !conv.archived && (!conv.private || conv.can.manage);
  if (adding)
    return (
      <Dialog
        title={t('ds.add_people')}
        onClose={() => setAdding(null)}
        footer={
          <>
            <Button variant="ghost" onClick={() => setAdding(null)}>
              {t('ds.cancel')}
            </Button>
            <Button variant="primary" disabled={!adding.length} onClick={async () => (await act('discuss.add_members', { id: conv.id, users: adding }, t('ds.added'))) && setAdding(null)}>
              {t('ds.add')}
            </Button>
          </>
        }
      >
        <PeoplePicker people={home.people} selected={adding} onChange={setAdding} exclude={conv.members.map((m) => m.id)} />
      </Dialog>
    );
  return (
    <Dialog
      title={tn('ds.members_n', conv.members.length)}
      onClose={onClose}
      footer={
        canAdd ? (
          <Button icon="plus" onClick={() => setAdding([])}>
            {t('ds.add_people')}
          </Button>
        ) : undefined
      }
    >
      <ul className="ds-people">
        {conv.members.map((p) => (
          <li key={p.id} className="ds-person">
            <Avatar id={p.id} name={p.name} online={p.online} size="sm" />
            <span className="ds-person-name">
              <strong>
                {p.name}
                {p.id === home.me ? ` ${t('ds.you')}` : ''}
              </strong>
              <span className="muted">
                @{p.username}
                {p.role === 'owner' && conv.kind === 'channel' ? ` · ${t('ds.owner')}` : ''}
              </span>
            </span>
            {conv.kind === 'channel' && conv.can.manage && p.id !== home.me ? (
              <span className="ds-person-acts">
                <Button size="sm" variant="ghost" onClick={() => void act('discuss.set_member', { id: conv.id, user: p.id, role: p.role === 'owner' ? 'member' : 'owner' })}>
                  {p.role === 'owner' ? t('ds.make_member') : t('ds.make_owner')}
                </Button>
                {!conv.everyone ? (
                  <IconButton icon="x" label={t('ds.remove_person', { name: p.name })} onClick={() => void act('discuss.set_member', { id: conv.id, user: p.id, role: null })} />
                ) : null}
              </span>
            ) : null}
          </li>
        ))}
      </ul>
    </Dialog>
  );
}

function Pins({ conv, people, me, onClose, act }: { conv: Conv; people: Map<string, Person>; me: string; onClose: () => void; act: Act }) {
  const [items, setItems] = useState<Msg[] | null>(null);
  const { fail } = useUi();
  const load = useCallback(() => action<{ items: Msg[] }>('discuss.pinned', { channel: conv.id }).then((r) => setItems(r.items), fail), [conv.id, fail]);
  useEffect(() => {
    void load();
  }, [load]);
  return (
    <Dialog title={t('ds.pinned_title')} onClose={onClose}>
      {!items ? (
        <Skeleton h={120} />
      ) : !items.length ? (
        <EmptyState icon="pushpin" title={t('ds.no_pins')} body={conv.can.pin ? t('ds.no_pins_body') : t('ds.no_pins_body_member')} />
      ) : (
        <ul className="ds-cards">
          {items.map((m) => (
            <li key={m.id} className="ds-card static">
              <span className="ds-card-where">
                <strong>{m.author_name}</strong>
                <time dateTime={m.at}>{since(m.at)}</time>
                {conv.can.pin ? (
                  <Button size="sm" variant="ghost" onClick={async () => (await act('discuss.pin', { id: m.id, pinned: false }, t('ds.unpinned'))) && void load()}>
                    {t('ds.unpin')}
                  </Button>
                ) : null}
              </span>
              <span className="ds-text">
                <Body text={m.body ?? ''} people={people} me={me} />
              </span>
            </li>
          ))}
        </ul>
      )}
    </Dialog>
  );
}

function ChannelSettings({ conv, home, onClose, act, onLeft }: { conv: Conv; home: Home; onClose: () => void; act: Act; onLeft: () => void }) {
  const [name, setName] = useState(conv.name);
  const [description, setDescription] = useState(conv.description ?? '');
  const [priv, setPriv] = useState(conv.private);
  const [everyone, setEveryone] = useState(conv.everyone);
  const [confirm, setConfirm] = useState<'leave' | 'archive' | null>(null);
  const clean = name.trim().replace(/^#+/, '').trim();
  const save = async () => {
    if (await act('discuss.update_channel', { id: conv.id, name: clean, description, private: priv, everyone }, t('ds.saved_settings'))) onClose();
  };
  return (
    <Dialog
      title={t('ds.settings_title', { name: conv.name })}
      onClose={onClose}
      footer={
        conv.can.manage ? (
          <>
            <Button variant="ghost" onClick={onClose}>
              {t('ds.cancel')}
            </Button>
            <Button variant="primary" disabled={!clean} onClick={() => void save()}>
              {t('ds.save')}
            </Button>
          </>
        ) : undefined
      }
    >
      {conv.can.manage ? (
        <>
          <Field label={t('ds.f_name')} required>
            <input className="input" value={name} maxLength={41} onChange={(e) => setName(e.target.value)} />
          </Field>
          <Field label={t('ds.f_description')}>
            <input className="input" value={description} maxLength={300} onChange={(e) => setDescription(e.target.value)} />
          </Field>
          <div className="ds-setting">
            <span>
              <strong>{t('ds.f_private')}</strong>
              <span className="muted small">{priv ? t('ds.f_private_on') : t('ds.f_private_off')}</span>
            </span>
            <Switch checked={priv} onChange={(v) => (setPriv(v), v && setEveryone(false))} label={t('ds.f_private')} disabled={everyone} />
          </div>
          {home.can.manage ? (
            <div className="ds-setting">
              <span>
                <strong>{t('ds.f_everyone')}</strong>
                <span className="muted small">{t('ds.f_everyone_hint')}</span>
              </span>
              <Switch checked={everyone} onChange={(v) => (setEveryone(v), v && setPriv(false))} label={t('ds.f_everyone')} />
            </div>
          ) : null}
        </>
      ) : (
        <p className="muted">{conv.description ?? t('ds.no_description')}</p>
      )}
      <div className="ds-danger">
        {!conv.everyone ? (
          <Button icon="logout" onClick={() => setConfirm('leave')}>
            {t('ds.leave')}
          </Button>
        ) : null}
        {conv.can.manage && !conv.everyone ? (
          <Button icon="archive" variant="danger" onClick={() => setConfirm('archive')}>
            {t('ds.archive')}
          </Button>
        ) : null}
      </div>
      {confirm ? (
        <Confirm
          danger={confirm === 'archive'}
          title={confirm === 'leave' ? t('ds.leave_q', { name: conv.name }) : t('ds.archive_q', { name: conv.name })}
          body={confirm === 'leave' ? (conv.private ? t('ds.leave_body_private') : t('ds.leave_body')) : t('ds.archive_body')}
          confirm={confirm === 'leave' ? t('ds.leave') : t('ds.archive')}
          onClose={() => setConfirm(null)}
          onConfirm={async () => {
            if (confirm === 'leave') {
              if (await act('discuss.leave', { id: conv.id }, t('ds.left'))) onLeft();
            } else if (await act('discuss.archive', { id: conv.id, archived: true }, t('ds.archived_ok'))) {
              setConfirm(null);
              onClose();
            }
          }}
        />
      ) : null}
    </Dialog>
  );
}
